import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../services/api";

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtDate(d) { if (!d) return "—"; return new Date(d).toLocaleDateString("fr-FR"); }

const FREQ_LABEL = { mensuel: "Mensuel", trimestriel: "Trimestriel", annuel: "Annuel", personnalise: "Personnalisé" };

export default function DashboardTab({ currentCompanyId, stats, onRefresh }) {
  const [dueSoon, setDueSoon] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [loadingExtra, setLoadingExtra] = useState(true);

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoadingExtra(true);
    Promise.all([
      api.subscriptions.dueSoon(currentCompanyId, 7),
      api.subscriptions.dueSoon(currentCompanyId, 30),
    ]).then(([week, month]) => {
      setDueSoon(week);
      setExpiring(month.filter(s => s.end_date));
    }).catch(console.error)
      .finally(() => setLoadingExtra(false));
  }, [currentCompanyId]);

  if (!stats) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  const churnColor  = stats.churn_rate > 10 ? "var(--red)" : stats.churn_rate > 5 ? "var(--amber)" : "var(--g)";
  const mrrHistory  = stats.mrr_history || [];

  return (
    <div>
      {/* KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 20, gridTemplateColumns: "repeat(5, 1fr)" }}>
        <div className="kpi">
          <div className="kpi-label">💹 MRR</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmt(stats.mrr)} €</div>
          <div className="kpi-sub up">Revenu mensuel récurrent</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">📈 ARR</div>
          <div className="kpi-value">{fmt(stats.arr)} €</div>
          <div className="kpi-sub">Revenu annuel récurrent</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">👥 Abonnés actifs</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{stats.active_count}</div>
          <div className="kpi-sub">{stats.total_count} total · taux {stats.renewal_rate}%</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">📅 Expirant ce mois</div>
          <div className="kpi-value" style={{ color: expiring.length > 0 ? "#ba7517" : "inherit" }}>
            {loadingExtra ? "…" : expiring.length}
          </div>
          <div className="kpi-sub" style={{ color: expiring.length > 0 ? "#ba7517" : undefined }}>
            {expiring.length > 0 ? "à renouveler" : "aucun"}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-label">📉 Churn du mois</div>
          <div className="kpi-value" style={{ color: churnColor }}>{stats.churn_rate}%</div>
          <div className="kpi-sub" style={{ color: churnColor }}>
            {stats.churned_count} résiliation(s)
          </div>
        </div>
      </div>

      <div className="g2">
        {/* Graphique MRR 12 mois */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">📊 Évolution MRR — 12 derniers mois</span>
          </div>
          {mrrHistory.length === 0 || mrrHistory.every(m => m.mrr === 0) ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)", fontSize: 13 }}>
              Aucun abonnement actif sur la période
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={mrrHistory} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e2e8f0)" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `${v.toLocaleString("fr-FR")} €`} labelStyle={{ color: "#333" }} />
                <Line type="monotone" dataKey="mrr" stroke="#1a9e72" strokeWidth={2} dot={{ r: 3, fill: "#1a9e72" }} name="MRR" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Alertes expirations */}
          {expiring.length > 0 && (
            <div className="card" style={{ border: "1px solid #ba751733" }}>
              <div style={{ fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <span>⚠️</span> Expirations dans 30 jours
                <span style={{ marginLeft: "auto", fontSize: 11, background: "#ba751722", color: "#ba7517", padding: "2px 8px", borderRadius: 8, fontWeight: 700 }}>{expiring.length}</span>
              </div>
              {expiring.slice(0, 5).map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "var(--border)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.customer_name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.name}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#ba7517" }}>{fmtDate(s.end_date)}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{fmt(s.amount_ttc)} € TTC</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Prochaines échéances cette semaine */}
          <div className="card">
            <div style={{ fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <span>📅</span> Échéances cette semaine
              {!loadingExtra && (
                <span style={{ marginLeft: "auto", fontSize: 11, background: "#1a9e7222", color: "var(--g)", padding: "2px 8px", borderRadius: 8, fontWeight: 700 }}>{dueSoon.length}</span>
              )}
            </div>
            {loadingExtra ? (
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Chargement…</div>
            ) : dueSoon.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Aucune échéance cette semaine</div>
            ) : (
              dueSoon.map(s => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "var(--border)" }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--g)", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{s.customer_name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.name} · {FREQ_LABEL[s.frequency]}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{fmtDate(s.next_billing_date)}</div>
                    <div style={{ fontSize: 11, color: "var(--g)" }}>{fmt(s.amount_ttc)} € TTC</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
